import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API_URL from '../../config/api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

.tfp-wrap {
  min-height: 100vh;
  background: linear-gradient(160deg, #064e3b 0%, #0d9488 40%, #0369a1 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
}
.tfp-wrap::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  border-radius: 50%;
}
.tfp-wrap::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(3,105,161,0.25) 0%, transparent 70%);
  border-radius: 50%;
}
.tfp-card {
  background: #fff;
  border-radius: 24px;
  padding: 44px 40px;
  max-width: 420px;
  width: 100%;
  box-shadow:
    0 20px 0 rgba(6,78,59,0.2),
    0 32px 64px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.9);
  position: relative;
  z-index: 1;
}
.tfp-icon-wrap {
  width: 68px; height: 68px;
  background: linear-gradient(135deg, #064e3b, #0d9488, #0369a1);
  border-radius: 18px;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(13,148,136,0.35);
}
.tfp-title {
  margin: 0 0 4px;
  font-size: 24px; font-weight: 800; color: #0f172a;
  text-align: center; letter-spacing: -0.02em;
}
.tfp-subtitle {
  margin: 0 0 32px;
  font-size: 14px; color: #64748b;
  text-align: center; font-weight: 500; line-height: 1.5;
}
.tfp-label {
  display: block; font-size: 13px; font-weight: 700;
  color: #374151; margin-bottom: 6px;
}
.tfp-input {
  width: 100%; padding: 12px 14px;
  border: 1.5px solid #d1d5db; border-radius: 10px;
  font-size: 15px; outline: none;
  font-family: inherit; box-sizing: border-box;
  transition: border-color 0.15s;
  color: #0f172a;
}
.tfp-input:focus { border-color: #0d9488; box-shadow: 0 0 0 3px rgba(13,148,136,0.1); }
.tfp-btn {
  width: 100%;
  background: linear-gradient(135deg, #0d9488, #0891b2);
  color: #fff; border: none; border-radius: 11px;
  padding: 14px; font-size: 15px; font-weight: 800;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 0 rgba(6,78,59,0.3), 0 8px 20px rgba(13,148,136,0.35);
  transition: opacity 0.15s, transform 0.12s;
  letter-spacing: -0.01em;
}
.tfp-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.tfp-btn:active:not(:disabled) { transform: translateY(1px); box-shadow: 0 2px 0 rgba(6,78,59,0.3); }
.tfp-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; }
.tfp-error {
  background: #fef2f2; border: 1.5px solid #fca5a5;
  border-radius: 9px; padding: 10px 14px;
  margin-bottom: 20px; font-size: 13px; color: #dc2626;
  display: flex; align-items: center; gap: 8px;
}
.tfp-success {
  background: #f0fdf4; border: 1.5px solid #86efac;
  border-radius: 9px; padding: 16px 14px;
  font-size: 14px; color: #166534;
  text-align: center; line-height: 1.5;
}
.tfp-hint {
  text-align: center; margin-top: 22px;
  font-size: 13px; color: #64748b; line-height: 1.5;
}
.tfp-hint a { color: #0d9488; font-weight: 700; text-decoration: none; }
.tfp-hint a:hover { text-decoration: underline; }
.tfp-footer {
  position: relative; z-index: 1;
  margin-top: 20px; font-size: 12px;
  color: rgba(255,255,255,0.7); text-align: center;
}
.tfp-footer a { color: #a7f3d0; text-decoration: none; font-weight: 600; }
.tfp-footer a:hover { color: #fff; }
`;

const TenantForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tenant-portal/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong. Please try again.'); return; }
      setSent(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tfp-wrap">
      <style>{CSS}</style>

      <div className="tfp-card">
        <div className="tfp-icon-wrap">🔑</div>
        <h1 className="tfp-title">Forgot password?</h1>
        <p className="tfp-subtitle">
          Enter your email and we'll send you a link to reset your Tenant Portal password.
        </p>

        {sent ? (
          <div className="tfp-success">
            ✅ <strong>Check your email.</strong><br />
            If an account exists for <strong>{email}</strong>, you'll receive a password reset link within a few minutes.
          </div>
        ) : (
          <>
            {error && (
              <div className="tfp-error">
                <span>⚠️</span> {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 26 }}>
                <label className="tfp-label">Email address</label>
                <input
                  className="tfp-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  autoComplete="email"
                />
              </div>
              <button type="submit" className="tfp-btn" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Link →'}
              </button>
            </form>
          </>
        )}

        <p className="tfp-hint">
          <Link to="/tenant/login">← Back to sign in</Link>
        </p>
      </div>

      <p className="tfp-footer">
        Are you a landlord?{' '}
        <Link to="/login">Sign in here</Link>
      </p>
    </div>
  );
};

export default TenantForgotPassword;
